import { LOTTE_CATEGORY_FIELD } from '@/constants/lotte-api/category';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import type { BuildingInStoreInfo, FloorInfo } from '@/services/dto';
import type { ShopFilterConfig } from '@/stores/types/shopStore';
import type { PoiContentSlide } from '@/types/shop';
import { DUTY_FREE_BUILDING_ID, getPoiBuildingName, isDutyFreePoi } from '@/utils/duty-free';
import poiUtils from '@/utils/poi';

const getFloorCode = (floor: FloorInfo): string => (floor as FloorInfo & { floor_code?: string }).floor_code ?? floor.floorCode;

const matchFloor = (shop: PoiContentSlide, config: ShopFilterConfig): boolean => {
    const { selectedFloor, buildings } = config;

    if (selectedFloor?.id === 'all') return true;

    if (Array.isArray(selectedFloor?.data)) {
        return (selectedFloor.data as string[]).includes(shop.data.studioFloorId);
    }

    if (selectedFloor?.data) {
        const floorInfo = selectedFloor.data as FloorInfo;
        const building = buildings?.find((b) => b.studioBuildingId === shop.data.studioBuildingId);
        const poiFloorName = shop.data.contents.cstrFlrNm?.ko || poiUtils.getFloorName('ko', shop.data.studioFloorId, building?.floors ?? []);
        const selectedFloorName = floorInfo.name?.ko || '';

        if (poiFloorName && selectedFloorName) {
            return poiFloorName === selectedFloorName;
        }

        return shop.data.studioFloorId === getFloorCode(floorInfo);
    }

    return false;
};

export const convertPoiContentToSlide = (lang: string, buildings: BuildingInStoreInfo[], searchText?: string): PoiContentSlide[] => {
    const seenPoiIds = new Set<string>();
    const allPois = buildings.flatMap((building) =>
        (building.filterPoiContents.shop ?? []).flatMap((poi) => {
            if (seenPoiIds.has(poi.id)) return [];
            seenPoiIds.add(poi.id);
            return [{ poi, building }];
        })
    );

    return allPois.map(({ poi, building }) => {
        const contents = poi.contents;

        const formatData = {
            name: poi.name?.[lang] ? poi.name?.[lang] : poi.name?.['ko'],
            shopUrl: contents.shopTmrkImgUrlApp?.[lang] ? contents.shopTmrkImgUrlApp?.[lang] : contents.shopTmrkImgUrlApp?.['ko'],
            buildingName: building?.name?.[lang] ? building?.name?.[lang] : building?.name?.['ko'],
        };

        const name = formatData.name || '';
        const shopUrl = formatData.shopUrl || '';
        const floor = contents.cstrFlrNm?.[lang] || poiUtils.getFloorName(lang, poi.studioFloorId, building.floors ?? []);
        const isDutyFree = isDutyFreePoi(poi, lang);
        const buildingName = getPoiBuildingName(poi, lang, formatData.buildingName || '');

        const rootFnbCategory = building.buildingCategories.find((c) => c.clientCode === LOTTE_CATEGORY_FIELD.SMALL_CLASSIFICATION_CODE);
        const rootFacilityCategory = building.buildingCategories.find((c) => c.clientCode === LOTTE_CATEGORY_FIELD.MEDIUM_CLASSIFICATION_CODE);
        const isFnb = poi.rootCategories.includes(rootFnbCategory?.categoryCode || '');
        const isFacility = poi.rootCategories.includes(rootFacilityCategory?.categoryCode || '');

        const finalCategory = isFnb
            ? building.filterCategories.fnb.find((f) => poi.categories.includes(f.categoryCode))
            : isFacility
              ? building.filterCategories.facility.find((f) => poi.categories.includes(f.categoryCode))
              : building.filterCategories.shop.find((f) => poi.categories.includes(f.categoryCode));

        const poiType = isFnb ? 'fnb' : isFacility ? 'facility' : 'normal';
        const matchingKeyword = searchText ? poiUtils.extractMatchingKeyword(contents.crltSrchKwrd || {}, searchText, lang) : undefined;

        return {
            id: poi.id,
            name,
            keyword: matchingKeyword,
            buildingName,
            floor,
            url: shopUrl,
            data: poi,
            poiType,
            category: finalCategory ?? undefined,
            isDutyFreePoi: isDutyFree,
        };
    });
};

export const filterByFloor = (shop: PoiContentSlide, config: ShopFilterConfig): boolean => {
    const { selectedBuildingId, storeCode, buildingCode, selectedFloor, buildings, language } = config;

    if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
        if (!isDutyFreePoi(shop.data, language)) return false;
        return matchFloor(shop, config);
    }

    if (selectedBuildingId && selectedBuildingId !== 'all' && isDutyFreePoi(shop.data, language)) {
        return false;
    }

    if (storeCode != null && buildingCode != null) {
        const poiCstrCd = shop.data.contents.cstrCd?.ko || '';
        const poiCstrTownCd = shop.data.contents.cstrTownCd?.ko || '';

        if (poiCstrCd !== storeCode || poiCstrTownCd !== buildingCode) {
            return false;
        }

        if (selectedFloor?.id === 'all') {
            return true;
        }

        const building = buildings?.find((b) => b.storeCode === storeCode && b.buildingCode === buildingCode);
        if (!building) return false;

        const poiFloorName = shop.data.contents.cstrFlrNm?.ko || poiUtils.getFloorName('ko', shop?.data?.studioFloorId, building.floors) || '';
        const selectedFloorInfo = selectedFloor?.data as FloorInfo;
        if (!selectedFloorInfo) return false;

        const selectedFloorName = selectedFloorInfo.name?.ko || '';
        return poiFloorName === selectedFloorName;
    }

    if (selectedBuildingId && selectedBuildingId !== 'all') {
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;

        if (selectedFloor?.id === 'all') {
            return shop.data.studioBuildingId === building.studioBuildingId;
        }

        return shop.data.studioFloorId === (selectedFloor?.data as FloorInfo)?.floorCode && shop.data.studioBuildingId === building.studioBuildingId;
    }

    if (selectedBuildingId === 'all') {
        if (selectedFloor?.id === 'all') return true;

        if (Array.isArray(selectedFloor?.data)) {
            return (selectedFloor.data as string[]).includes(shop.data.studioFloorId);
        }

        return false;
    }

    return false;
};

export const filterByCategory = (shop: PoiContentSlide, config: ShopFilterConfig): boolean => {
    const { selectedCategory, storeCode, buildingCode, selectedBuildingId, buildings, language } = config;

    if (!selectedCategory) return false;

    if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
        if (!isDutyFreePoi(shop.data, language)) return false;
    } else if (storeCode != null && buildingCode != null) {
        const poiCstrCd = shop.data.contents.cstrCd?.ko || '';
        const poiCstrTownCd = shop.data.contents.cstrTownCd?.ko || '';

        if (poiCstrTownCd !== buildingCode || poiCstrCd !== storeCode) {
            return false;
        }
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        if (isDutyFreePoi(shop.data, language)) return false;

        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (shop.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (selectedCategory.clientCode === 'all') {
        return true;
    }

    const building = buildings?.find((b) => b.studioBuildingId === shop.data.studioBuildingId);

    if (buildings?.length === 1 && selectedCategory.clientCode === LOTTE_CATEGORY_FIELD.MEDIUM_CLASSIFICATION_CODE) {
        return shop.data.rootCategories.includes(selectedCategory.categoryCode || '');
    }

    if (selectedCategory.categoryCode) {
        return shop.data.categories.includes(selectedCategory.categoryCode);
    }

    const foundCategory = building?.buildingCategories.find((c) => c.clientCode === selectedCategory.clientCode);
    if (foundCategory?.clientCode === LOTTE_CATEGORY_FIELD.MEDIUM_CLASSIFICATION_CODE) {
        return shop.data.rootCategories.includes(foundCategory?.categoryCode || '');
    }

    return shop.data.categories.includes(foundCategory?.categoryCode || '');
};

export const filterByText = (shop: PoiContentSlide, config: ShopFilterConfig, keyboardLanguage: 'ko' | 'en'): boolean => {
    const { searchText, language, storeCode, buildingCode, selectedBuildingId, buildings } = config;

    if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
        if (!isDutyFreePoi(shop.data, language)) return false;
    } else if (storeCode != null && buildingCode != null) {
        const poiCstrCd = shop.data?.contents?.cstrCd?.ko || '';
        const poiCstrTownCd = shop.data?.contents?.cstrTownCd?.ko || '';

        if (poiCstrTownCd !== buildingCode || poiCstrCd !== storeCode) {
            return false;
        }
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        if (isDutyFreePoi(shop.data, language)) return false;

        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (shop.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (!searchText?.trim()) return true;

    const nameKeyboard = shop.data.name[keyboardLanguage] || '';
    const keywordKeyboard = (shop.data.contents.crltSrchKwrd['ko'] || '').replaceAll('#', '');
    const nameDefault = shop.data.name[language] || '';
    const keywordDefault = (shop.data.contents.crltSrchKwrd['ko'] || '').replaceAll('#', '');
    const normalizedSearchText = searchText.trim();

    return (
        poiUtils.searchByText(nameKeyboard, normalizedSearchText) ||
        poiUtils.searchByText(keywordKeyboard, normalizedSearchText) ||
        poiUtils.searchByText(nameDefault, normalizedSearchText) ||
        poiUtils.searchByText(keywordDefault, normalizedSearchText)
    );
};

export const createShopFilter = (searchMode: SHOP_SEARCH_MODE, config: ShopFilterConfig, keyboardLanguage: 'ko' | 'en') => {
    const filterMap = {
        [SHOP_SEARCH_MODE.SEARCH_BY_FLOOR]: filterByFloor,
        [SHOP_SEARCH_MODE.SEARCH_BY_CATEGORY]: filterByCategory,
        [SHOP_SEARCH_MODE.SEARCH_BY_TEXT]: filterByText,
    };

    const filterFunction = filterMap[searchMode];

    return (shop: PoiContentSlide) => {
        return filterFunction ? filterFunction(shop, config, keyboardLanguage) : true;
    };
};

const shopUtils = {
    convertPoiContentToSlide,
    createShopFilter,
    filterByFloor,
    filterByCategory,
    filterByText,
};

export default shopUtils;
